import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { SocketContext } from '@/lib/providers/socket-provider'
import { useAuth } from '@/store/hooks'
import {
    getPresencePanel,
    getPresenceStatuses,
    PresenceEntry,
    PresenceStatus,
} from '@/lib/api/presence/presence-api'

/**
 * Painel de presença ao vivo.
 *
 * Uma única fonte para o indicador do navbar, o modal e a tela cheia: os três
 * ouvem o mesmo evento de socket (`presence-updated-<companyId>`) e recarregam
 * a lista inteira. Recarregar tudo — em vez de aplicar o delta do payload —
 * mantém a ordenação por posição do controle correta mesmo quando dois
 * operadores mexem no painel ao mesmo tempo, que é o caso normal no balcão.
 */
export function usePresence(options?: { autoLoad?: boolean }) {
    const autoLoad = options?.autoLoad ?? true
    const { socket, isConnected } = useContext(SocketContext)
    const { user } = useAuth()

    const [entries, setEntries] = useState<PresenceEntry[]>([])
    const [statuses, setStatuses] = useState<PresenceStatus[]>([])
    const [loading, setLoading] = useState(autoLoad)
    /** Atualizações chegadas desde a última vez que o usuário abriu o painel. */
    const [unseenCount, setUnseenCount] = useState(0)
    const [lastUpdateAt, setLastUpdateAt] = useState<string | null>(null)

    // Evita corrida entre recargas disparadas em sequência pelo socket: só a
    // resposta da última chamada é aplicada.
    const requestRef = useRef(0)

    const loadPanel = useCallback(async () => {
        const requestId = ++requestRef.current
        const response = await getPresencePanel()
        if (requestId !== requestRef.current) return
        if (response.success && response.result) {
            setEntries(response.result)
        }
    }, [])

    const loadStatuses = useCallback(async () => {
        const response = await getPresenceStatuses(true)
        if (response.success && response.result) {
            setStatuses(response.result)
        }
    }, [])

    const reload = useCallback(async () => {
        setLoading(true)
        await Promise.all([loadPanel(), loadStatuses()])
        setLoading(false)
    }, [loadPanel, loadStatuses])

    useEffect(() => {
        if (!autoLoad) return
        reload()
    }, [autoLoad, reload])

    useEffect(() => {
        if (!socket || !isConnected || !user?.companyId) return

        const event = `presence-updated-${user.companyId}`
        const handler = (data: { action?: string; updatedAt?: string }) => {
            setLastUpdateAt(data?.updatedAt ?? new Date().toISOString())
            // Mudança de controle da empresa não é "paciente novo no balcão":
            // atualiza a lista sem acender o indicador.
            if (data?.action?.startsWith('ENTRY')) {
                setUnseenCount(count => count + 1)
            }
            loadPanel()
            if (data?.action?.startsWith('STATUS')) {
                loadStatuses()
            }
        }

        socket.on(event, handler)
        return () => {
            socket.off(event, handler)
        }
    }, [socket, isConnected, user?.companyId, loadPanel, loadStatuses])

    const markSeen = useCallback(() => setUnseenCount(0), [])

    return {
        entries,
        statuses,
        loading,
        unseenCount,
        lastUpdateAt,
        reload,
        loadPanel,
        loadStatuses,
        markSeen,
    }
}
